import { useMemo } from "react"
import { type UseQueryExecute, useQuery } from "urql"

import type { Instruction, InstructionFailure, InstructionListSuccess } from "@/graphql/codegen/graphql"
import { ProductInstructionsListDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetProductInstructionsReturn {
  productInstructions: Instruction[]
  isLoading: boolean
  refetchInstructionsList: UseQueryExecute
}

const useGetProductInstructions = ({ productId }: { productId: number }): UseGetProductInstructionsReturn => {
  const [{ data, fetching, error }, refetchInstructionsList] = useQuery({
    query: ProductInstructionsListDocument,
    variables: {
      productId,
    },
  })
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const productInstructions = useMemo(() => {
    if (!data) return []

    if (data.instruction.listProductInstructions.__typename === "InstructionFailure" || error) {
      notify({
        message: "Failed to fetch product instructions",
        content: error?.message ?? (data.instruction.listProductInstructions as InstructionFailure).error.message,
        type: NotificationType.Error,
      })
      return []
    }

    return (data.instruction.listProductInstructions as InstructionListSuccess).instructions ?? []
  }, [data, notify, error])

  return {
    productInstructions,
    isLoading: fetching,
    refetchInstructionsList,
  }
}

export default useGetProductInstructions
